import { useQuery } from '@tanstack/react-query';
import { AlarmClock, Settings } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { DateRangeProvider } from './context/DateRangeContext';
import { InsightsPageTabsEnum } from './insightsPageTabs';
import { QueryKey } from '@/Config/api/queryKey';
import { AssistedRevenueAvailabilityDto } from '@/Domain/api/AssistedOrders/AssistedOrders.dto';
import { ReadDocumentationButton } from '@/Domain/components/Integration/components/ReadDocumentationButton';
import { useTrackPageVisit } from '@/Domain/components/OnboardingProgress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/Domain/components/Shared/Tabs/Tabs';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';
import { useScrollEdgeFade } from '@/Domain/hooks/Shared/use-scroll-edge-fade';
import { AiInsightsLoader } from '@/UI/Insights/AiInsights/AiInsights.loader';
import { AnalyticsInsightsLoader } from '@/UI/Insights/Analytics/AnalyticsInsights.loader';
import { AssistedRevenueLoader } from '@/UI/Insights/AssistedRevenue/AssistedRevenue.loader';
import { ConversationRatingLoader } from '@/UI/Insights/ConversationRating/ConversationRating.loader';
import { KBSourcesLoader } from '@/UI/Insights/KBSources/KBSources.loader';
import { ManageAlertsLoader } from '@/UI/Insights/ManageAlerts/ManageAlerts.loader';
import { ManageTopicsLoader } from '@/UI/Insights/ManageTopics/ManageTopics.loader';
import { UnifiedAiInsightsLoader } from '@/UI/Insights/UnifiedAiInsights/UnifiedAiInsights.loader';
import { UnreadCountBadge } from '@/UI/Insights/UnifiedAiInsights/components/UnreadCountBadge';

const KNOWN_TABS: string[] = Object.values(InsightsPageTabsEnum);
const UNKNOWN_TAB = 'unknown';

export { InsightsPageTabsEnum };

export const InsightsPage = () => {
  const { t } = useTranslation('insights_page');
  useTrackPageVisit('insights');
  const [searchParams, setSearchParams] = useSearchParams();
  const currentTab = searchParams.get('tab') || InsightsPageTabsEnum.Analytics;

  const { data: unreadData } = useQuery<{ unread: number; capped: boolean }>({
    queryKey: [QueryKey.analytics.unreadInsightsCount],
  });
  const unread = unreadData?.unread ?? 0;
  const capped = unreadData?.capped ?? false;

  // Offer the AI-Assisted Revenue tab to accounts with a connected store (so a
  // newly-integrated merchant sees the zero state before their first order) or
  // any attributed orders (so a since-disconnected store keeps its history).
  // Non-ecommerce accounts never see it.
  const { data: assistedRevenueAvailability, isFetching: isAssistedRevenueAvailabilityFetching } =
    useQuery<AssistedRevenueAvailabilityDto>({
      queryKey: [QueryKey.assistedOrders.availability],
    });
  const hasAssistedRevenue = assistedRevenueAvailability?.available ?? false;

  const { capture } = usePostHogAnalytics();

  // Capture the resolved tab so landing, deep-link and history navigation share one path.
  const initialTabRef = useRef(currentTab);
  const lastCapturedTabRef = useRef<string | null>(null);
  useEffect(() => {
    if (
      lastCapturedTabRef.current === currentTab ||
      (currentTab === InsightsPageTabsEnum.AssistedRevenue &&
        !hasAssistedRevenue &&
        isAssistedRevenueAvailabilityFetching)
    )
      return;
    const isLandingTab =
      lastCapturedTabRef.current === null && currentTab === initialTabRef.current;
    lastCapturedTabRef.current = currentTab;
    const isAvailableTab =
      KNOWN_TABS.includes(currentTab) &&
      (currentTab !== InsightsPageTabsEnum.AssistedRevenue || hasAssistedRevenue);
    capture(PostHogEventEnum.InsightsTabSelected, {
      page_tab: isAvailableTab ? currentTab : UNKNOWN_TAB,
      is_landing_tab: isLandingTab,
    });
  }, [currentTab, hasAssistedRevenue, isAssistedRevenueAvailabilityFetching, capture]);

  const handleTabChange = (value: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', value);
      return next;
    });
  };

  const { ref: tabsScrollerRef, maskImage: tabsMaskImage } = useScrollEdgeFade();

  return (
    <DateRangeProvider>
      <div className="max-w-7xl w-full mx-auto min-w-0 flex flex-1 flex-col">
        <div className="mb-6 flex items-center justify-between gap-4">
          <h1 className="font-semibold text-lg">{t('title')}</h1>
          <ReadDocumentationButton href="https://docs.quickchat.ai/conversations/insights" />
        </div>
        <Tabs value={currentTab} onValueChange={handleTabChange} className="flex flex-1 flex-col">
          {/* `TabsList` is `inline-flex` (sizes to content) so a long set of
              tabs would push the page wider than the viewport on mobile.
              Wrapping in an `overflow-x-auto max-w-full` container lets the
              tab strip scroll horizontally inside itself instead of leaking
              past the page edge. */}
          <div
            ref={tabsScrollerRef}
            style={
              tabsMaskImage
                ? { maskImage: tabsMaskImage, WebkitMaskImage: tabsMaskImage }
                : undefined
            }
            className="-mx-1 max-w-full overflow-x-auto px-1 pb-1"
          >
            <TabsList id="tabs-list">
              <TabsTrigger data-testid="analytics" value={InsightsPageTabsEnum.Analytics}>
                {t('tabs.analytics')}
              </TabsTrigger>
              <TabsTrigger data-testid="advanced" value={InsightsPageTabsEnum.Advanced}>
                {t('tabs.advanced')}
              </TabsTrigger>
              <TabsTrigger
                data-testid="unified-ai-insights"
                value={InsightsPageTabsEnum.UnifiedAiInsights}
                className="gap-0"
              >
                {t('tabs.unified_ai_insights')}
                <UnreadCountBadge
                  unread={unread}
                  capped={capped}
                  testId="unified-ai-insights-unread-badge"
                />
              </TabsTrigger>
              {hasAssistedRevenue && (
                <TabsTrigger
                  data-testid="assisted-revenue"
                  value={InsightsPageTabsEnum.AssistedRevenue}
                >
                  {t('tabs.assisted_revenue')}
                </TabsTrigger>
              )}
              <TabsTrigger data-testid="kb-sources" value={InsightsPageTabsEnum.KBSources}>
                {t('tabs.kb_sources')}
              </TabsTrigger>
              <TabsTrigger
                data-testid="conversation-rating"
                value={InsightsPageTabsEnum.ConversationRating}
              >
                {t('tabs.conversation_rating')}
              </TabsTrigger>
              <TabsTrigger data-testid="manage-topics" value={InsightsPageTabsEnum.ManageTopics}>
                <Settings className="text-sm -mr-0.5 size-4" />
                {t('tabs.manage_topics')}
              </TabsTrigger>
              <TabsTrigger data-testid="manage-alerts" value={InsightsPageTabsEnum.ManageAlerts}>
                <AlarmClock className="text-sm -mr-0.5 size-4" />
                {t('tabs.manage_alerts')}
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value={InsightsPageTabsEnum.Analytics}>
            <AnalyticsInsightsLoader />
          </TabsContent>
          <TabsContent value={InsightsPageTabsEnum.Advanced}>
            <AiInsightsLoader />
          </TabsContent>
          <TabsContent value={InsightsPageTabsEnum.UnifiedAiInsights}>
            <UnifiedAiInsightsLoader />
          </TabsContent>
          {hasAssistedRevenue && (
            <TabsContent value={InsightsPageTabsEnum.AssistedRevenue}>
              <AssistedRevenueLoader />
            </TabsContent>
          )}
          <TabsContent value={InsightsPageTabsEnum.KBSources}>
            <KBSourcesLoader />
          </TabsContent>
          <TabsContent
            value={InsightsPageTabsEnum.ConversationRating}
            className="flex flex-1 flex-col"
          >
            <ConversationRatingLoader />
          </TabsContent>
          <TabsContent value={InsightsPageTabsEnum.ManageTopics} className="flex flex-1 flex-col">
            <ManageTopicsLoader />
          </TabsContent>
          <TabsContent value={InsightsPageTabsEnum.ManageAlerts}>
            <ManageAlertsLoader />
          </TabsContent>
        </Tabs>
      </div>
    </DateRangeProvider>
  );
};
